import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import graphqlQuery from 'helpers/graphqlQuery'
import getSitesForStaticPaths from 'helpers/getSitesForStaticPaths'
import CONTACT_QUERY from 'graphql/contact.query'

import Layout from 'components/Layout'
import WordpressSEO from 'components/WordpressSEO'
import Loading from 'components/Loading'
import dynamic from 'next/dynamic'

import s from './contact.module.css'
import StateInitializer from '../../components/StateInitializer'
import getGlobalSettings from '../../helpers/getGlobalSettings'
import getSites from '../../helpers/getSites'
import { createApolloClient } from '../../lib/createApolloClient'
import * as Sentry from '@sentry/nextjs'

const DynamicGoogleMap = dynamic(() => import('components/GoogleMap'))

// todo: add propTypes
function Contact({
  globalSettings,
  sites,
  siteSlug,
  site = '',
  title: initialTitle = '',
  seo = {},
  offices: loadedOffices = {},
}) {
  const [activeSite, setActiveSite] = useState(site)
  const [loading, setLoading] = useState(false)
  const [offices, setOffices] = useState(loadedOffices)

  useEffect(() => {
    if (!offices[activeSite]) {
      // Kicks off an async data fetch in response to activeSite
      // changing, a genuine side effect rather than a derivable value.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoading(true)

      const fetchData = async (aSite) => {
        // todo: add some error handling here
        const { offices: newOffices } = await graphqlQuery(
          CONTACT_QUERY,
          activeSite
        )
        setOffices((cur) => ({
          ...cur,
          [aSite]: newOffices.nodes,
        }))
        setLoading(false)
      }
      fetchData(activeSite)
    }
  }, [activeSite, offices])

  if (loading) {
    return (
      <>
        <WordpressSEO url={`/${site}/contact`} title={initialTitle} seo={seo} />
        <Loading />
      </>
    )
  }

  if (!offices || !offices[activeSite]) return null

  const office = offices[activeSite][0]

  const {
    title: officeTitle,
    acfOffices: {
      address,
      contactDetails,
      contacts,
      latitude,
      longitude,
      mapLink,
    },
  } = office

  return (
    <StateInitializer
      globalSettings={globalSettings}
      sites={sites}
      siteSlug={siteSlug}
    >
      <Layout globalSettings={globalSettings}>
        <WordpressSEO url={`/${site}/contact`} title={officeTitle} seo={seo} />
        <div className={classNames(s.container)}>
          <div className={s.left}>
            <div className={s.content}>
              <h1 className={s.title}>{officeTitle}</h1>
              {contactDetails && (
                <div
                  className={s.contactDetails}
                  dangerouslySetInnerHTML={{ __html: contactDetails }}
                />
              )}
              <div className={s.boards}>
                {contacts?.map(({ title, link, contactInfo }) => (
                  <div key={title} className={s.board}>
                    {title && (
                      <h2 className={s.boardTitle}>
                        {link?.url ? (
                          <a href={link.url} className={s.boardLink}>
                            {title}
                          </a>
                        ) : (
                          title
                        )}
                      </h2>
                    )}
                    {contactInfo?.map(({ link, text }) => {
                      if (!link) return null
                      return link?.url ? (
                        <a
                          key={link.url}
                          href={link.url}
                          className={s.boardLink}
                        >
                          {text}
                        </a>
                      ) : (
                        <span key={link.url} className={s.boardLink}>
                          {text}
                        </span>
                      )
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={s.right}>
            <DynamicGoogleMap
              latitude={latitude}
              longitude={longitude}
              markerLink={mapLink}
              info={address}
            />
          </div>
        </div>
        {sites && (
          <div className={s.locationsWrapper}>
            <div className={s.locations}>
              {sites.map(({ id, name, path }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveSite(path.replace(/\//g, ''))}
                  className={classNames(s.location, {
                    [s.locationActive]:
                      name ===
                      activeSite.charAt(0).toUpperCase() + activeSite.slice(1),
                  })}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
        )}
      </Layout>
    </StateInitializer>
  )
}

Contact.propTypes = {
  globalSettings: PropTypes.object.isRequired,
  sites: PropTypes.array.isRequired,
  siteSlug: PropTypes.string.isRequired,
  seo: PropTypes.object,
  offices: PropTypes.object,
}

export default Contact

export async function getStaticProps({ params }) {
  const { site } = params

  const apolloClient = createApolloClient()

  const [globalSettings, sites, { data, error }] = await Promise.all([
    getGlobalSettings(site),
    getSites(),
    apolloClient.query({
      query: CONTACT_QUERY,
      context: { site },
    }),
  ])

  if (error) {
    Sentry.addBreadcrumb({
      category: 'getStaticProps',
      message: `Error loading Index props for site ${site}`,
      level: 'error',
      data: { params, error },
    })
    Sentry.captureException(new Error(`Error loading Index props - ${error}`))
    console.error(`Error loading Index props - ${error}`)
  }

  return {
    props: {
      ...params,
      ...data.pageBy,
      offices: {
        [site]: data.offices.nodes,
      },
      globalSettings,
      sites,
      siteSlug: site,
    },
    revalidate: 60 * 60 * 24, // Revalidate every 24 hours
  }
}

export async function getStaticPaths() {
  const sites = await getSitesForStaticPaths()

  const paths = sites.map((site) => ({
    params: {
      site,
    },
  }))

  return {
    paths,
    fallback: false,
  }
}
